import { useState, useCallback, useEffect, useRef } from 'react'

/**
 * @typedef {Object} UseMenuStateOptions
 * @property {number | null} [defaultOpenId=null]
 */

/**
 * Hook que maneja el estado abierto/cerrado del MegaMenu.
 * Coordina un solo panel abierto a la vez (openId global).
 * Maneja activeLevel2Id y el delay de 150ms para Nivel 3.
 * @param {UseMenuStateOptions} [options]
 * @returns {{
 *   openId: number | null,
 *   activeLevel2Id: number | null,
 *   activeLevel3: Array,
 *   isOpen: boolean,
 *   open: (id: number) => void,
 *   close: () => void,
 *   toggle: (id: number) => void,
 *   selectLevel2: (id: number, children?: Array) => void,
 *   clearLevel3: () => void,
 *   selectedItemId: number | string | null,
 *   selectItem: (id: number | string) => void,
 * }}
 */
export function useMenuState(options = {}) {
  const { defaultOpenId = null } = options

  const [openId, setOpenId] = useState(defaultOpenId)
  const [activeLevel2Id, setActiveLevel2Id] = useState(/** @type {number|null} */ (null))
  const [activeLevel3, setActiveLevel3] = useState(/** @type {Array} */ ([]))
  // Última opción elegida (hoja navegada). Persiste al cerrar/abrir el menú.
  const [selectedItemId, setSelectedItemId] = useState(/** @type {number|string|null} */ (null))

  const level3TimerRef = useRef(/** @type {ReturnType<typeof setTimeout>|null} */ (null))

  const close = useCallback(() => {
    setOpenId(null)
    setActiveLevel2Id(null)
    setActiveLevel3([])
  }, [])

  const open = useCallback(
    /** @param {number} id */ (id) => {
      setOpenId(id)
      setActiveLevel2Id(null)
      setActiveLevel3([])
    },
    []
  )

  const toggle = useCallback(
    /** @param {number} id */ (id) => {
      if (openId === id) {
        close()
      } else {
        open(id)
      }
    },
    [openId, open, close]
  )

  const selectLevel2 = useCallback(
    /**
     * @param {number} id
     * @param {Array} [children]
     */
    (id, children) => {
      if (level3TimerRef.current) {
        clearTimeout(level3TimerRef.current)
        level3TimerRef.current = null
      }

      const hasChildren = children && children.length > 0
      setActiveLevel2Id(id)

      if (hasChildren) {
        level3TimerRef.current = setTimeout(() => {
          setActiveLevel3(children)
          level3TimerRef.current = null
        }, 150)
      } else {
        setActiveLevel3([])
      }
    },
    []
  )

  const clearLevel3 = useCallback(() => {
    if (level3TimerRef.current) {
      clearTimeout(level3TimerRef.current)
      level3TimerRef.current = null
    }
    setActiveLevel2Id(null)
    setActiveLevel3([])
  }, [])

  const selectItem = useCallback(
    /** @param {number|string} id */
    (id) => {
      setSelectedItemId(id)
    },
    []
  )

  useEffect(() => {
    return () => {
      if (level3TimerRef.current) {
        clearTimeout(level3TimerRef.current)
      }
    }
  }, [])

  return {
    openId,
    activeLevel2Id,
    activeLevel3,
    isOpen: openId !== null,
    open,
    close,
    toggle,
    selectLevel2,
    clearLevel3,
    selectedItemId,
    selectItem,
  }
}
